/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import {
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  TextInput,
  TouchableHighlight,
  View,
} from 'react-native';
import PeerView from '../PeerView';
import DyteCameraToggle from './DyteCameraToggle';
import DyteMicToggle from './DyteMicToggle';
import DyteText from './DyteText';
import DyteSettingsToggle from './DyteSettingsToggle';
import { States } from '../types/props';

import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import DyteClient from '@dytesdk/web-core';
import { UIConfig } from '../types/ui-config';
import { defaultConfig } from '../lib/default-ui-config';
import { useDyteSelector } from '@dytesdk/react-native-core';
import { Peer } from '../types/dyte-client';
import { SvgXml } from 'react-native-svg';
import DytePermissionsMessage from './DytePermissionsMessage';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function DyteSetupScreen({
  meeting,
  config = defaultConfig,
  iconPack = defaultIconPack,
  size = 'sm',
  states,
  t,
}: {
  meeting: DyteClient;
  config?: UIConfig;
  iconPack?: IconPack;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  states?: States;
  t?: DyteI18n;
}) {
  const selfParticipant = useDyteSelector((m) => m.self);
  const viewType = useDyteSelector((m) => m.meta.viewType);
  const stageStatus = useDyteSelector((m) => m.stage.status);
  const acceptPresentRequests = useDyteSelector(
    (m) => m.self.permissions.acceptPresentRequests
  );
  const { width, height } = Dimensions.get('screen');
  const [dimensions, setDimensions] = useState({
    width: width,
    height: height,
  });
  useEffect(() => {
    const _listener = Dimensions.addEventListener('change', () => {
      const newDimensions = Dimensions.get('screen');
      setDimensions({
        width: newDimensions.width,
        height: newDimensions.height,
      });
    });
    return () => {
      _listener.remove();
    };
  }, []);
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const [displayName, onChangeText] = useState(
    selfParticipant.name === undefined ? ' ' : selfParticipant.name
  );
  if (displayName.trim() !== '') meeting?.self.setName(displayName);
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  states = states ?? storeStates;
  const waitlistStatus = useDyteSelector((m) => m.self.waitlistStatus);
  const join = () => {
    if (displayName.trim() !== '') {
      meeting?.self.setName(displayName.trim());
      meeting?.joinRoom().catch((e) => console.log(e));
      setStates({
        ...states,
        meeting: waitlistStatus !== 'accepted' ? 'waiting' : 'idle',
      });
      //setStates({ ...states, meeting: 'idle' });
    }
  };
  useEffect(() => {
    if (meeting.self.mediaPermissions.video !== 'ACCEPTED')
      meeting.self.disableVideo();
    if (meeting.self.mediaPermissions.audio !== 'ACCEPTED')
      meeting.self.disableAudio();
    // Initialise only audio for webinar/livestream viewer
    if (viewType !== 'GROUP_CALL' && stageStatus !== 'ON_STAGE')
      meeting.self.setupTracks({ audio: true, video: false });
    // Initialise audio, video  for webinar/livestream viewer
    if (viewType !== 'GROUP_CALL' && acceptPresentRequests)
      meeting.self.setupTracks({ audio: true, video: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (
      meeting.self.mediaPermissions.video === 'DENIED' &&
      meeting.self.mediaPermissions.audio === 'DENIED'
    ) {
      setTimeout(() => {
        setStates({
          ...storeStates,
          permissionGranted: false,
        });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting]);
  const keyboardVerticalOffset = Platform.OS === 'ios' ? 200 : 0;
  if (!storeStates.permissionGranted) {
    return <DytePermissionsMessage meeting={meeting} />;
  }
  const styles = StyleSheet.create({
    setupWindow: {
      // flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background[900],
    },
    controls: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 10,
    },
    iconButtons: {
      padding: 1,
      margin: 2,
      marginHorizontal: 10,
      backgroundColor: colors.background[800],
      borderRadius: 10,
      height: 50,
      width: 50,
      justifyContent: 'center',
      alignItems: 'center',
    },
    cameraSwitch: {
      position: 'absolute',
      right: 10,
      top: 10,
      padding: 4,
      borderRadius: 4,
      backgroundColor: colors.background[800],
    },
    joinButton: {
      height: '8%',
      width: '80%',
      margin: 12,
      marginTop: 20,
      backgroundColor: colors.brand[500],
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 10,
    },
    input: {
      height: 50,
      borderRadius: 5,
      borderWidth: 0.5,
      backgroundColor: colors.background[700],
      padding: 16,
      color: colors.text,
    },
    loadingIndicator: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    nameField: {
      width: '80%',
    },
    joinInText: {
      marginVertical: '3%',
    },
  });
  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={keyboardVerticalOffset}
    >
      <View style={styles.setupWindow}>
        <View>
          <PeerView
            participant={selfParticipant as unknown as Peer}
            width={dimensions.width * 0.8}
            height={dimensions.height * 0.5}
          />
          {selfParticipant.permissions.canProduceVideo === 'ALLOWED' &&
            meeting.self.mediaPermissions.video === 'ACCEPTED' &&
            meeting.self.videoEnabled && (
              <View style={{ ...styles.cameraSwitch }}>
                <TouchableHighlight
                  underlayColor={colors.background[800]}
                  children={
                    <SvgXml
                      xml={iconPack.video_flip.replace(
                        'currentColor',
                        colors.text
                      )}
                      height={28}
                      width={28}
                    />
                  }
                  onPress={() => {
                    selfParticipant.videoTrack._switchCamera();
                  }}
                />
              </View>
            )}
        </View>
        <View style={styles.controls}>
          {selfParticipant.permissions.canProduceAudio === 'ALLOWED' && (
            <View
              // eslint-disable-next-line react-native/no-inline-styles
              style={{
                ...styles.iconButtons,
                opacity:
                  meeting.self.mediaPermissions.audio === 'ACCEPTED'
                    ? 1.0
                    : 0.6,
                backgroundColor:
                  meeting.self.mediaPermissions.audio === 'ACCEPTED'
                    ? colors.background[800]
                    : colors.background[600],
              }}
            >
              <DyteMicToggle meeting={meeting} />
            </View>
          )}
          {selfParticipant.permissions.canProduceVideo === 'ALLOWED' && (
            <View
              // eslint-disable-next-line react-native/no-inline-styles
              style={{
                ...styles.iconButtons,
                opacity:
                  meeting.self.mediaPermissions.video === 'ACCEPTED'
                    ? 1.0
                    : 0.6,
                backgroundColor:
                  meeting.self.mediaPermissions.video === 'ACCEPTED'
                    ? colors.background[800]
                    : colors.background[600],
              }}
            >
              <DyteCameraToggle meeting={meeting} />
            </View>
          )}
          {(meeting.self.mediaPermissions.video === 'ACCEPTED' ||
            meeting.self.mediaPermissions.audio === 'ACCEPTED') && (
            <View style={styles.iconButtons}>
              <DyteSettingsToggle />
            </View>
          )}
        </View>
        <View style={styles.joinInText}>
          <DyteText size="lg">{t('setup_screen.join_in_as')}</DyteText>
        </View>
        <View style={styles.nameField}>
          <TextInput
            style={styles.input}
            editable={meeting.self.permissions.canEditDisplayName}
            onChangeText={(text) => {
              onChangeText(text);
              if (displayName.trim() !== '')
                meeting?.self.setName(displayName.trim());
            }}
            value={displayName}
          />
        </View>
        <TouchableHighlight
          underlayColor={colors.background[800]}
          style={styles.joinButton}
          onPress={join}
        >
          {/* eslint-disable-next-line react-native/no-inline-styles */}
          <View style={{ alignContent: 'center' }}>
            <DyteText onBrand>{t('join')}</DyteText>
          </View>
        </TouchableHighlight>
      </View>
    </KeyboardAvoidingView>
  );
}
